// ===================================================================
// BLACKBOARD — Communications
// Send messages via in-app inbox and/or email, with sent history
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Button, Textarea, TextInput, Select,
  Stack, SimpleGrid, MultiSelect, Progress, SegmentedControl, Badge,
  Tabs, Table,
} from '../../components/gab/GabComponents';
import {
  IconSend, IconMail, IconRefresh, IconUsers, IconInbox,
  IconHistory, IconChevronDown, IconChevronRight, IconCheck, IconClock,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import dayjs from 'dayjs';
import { useBlackboardStore, type BBMessage } from '../../store/blackboardStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { WORKER_URL } from '../../lib/api-switch';

const WORKER_BASE = WORKER_URL;
const ORIGIN_HEADER = 'https://apps.opengov.com';

type TargetMode = 'all' | 'role' | 'training' | 'class' | 'specific';
type Channel = 'inbox' | 'email' | 'both';

interface SendResult {
  total: number;
  sent: number;
  failed: number;
  errors: string[];
}

export default function Comms() {
  const { canEdit } = useModulePermission('blackboard');
  const { user } = useAuthStore();
  const {
    users, trainings, trainingRecords, classrooms, messages,
    loadUsers, loadTrainings, loadTrainingRecords, loadClassrooms,
    loadMessages, sendMessages,
  } = useBlackboardStore();

  const [activeTab, setActiveTab] = useState<string | null>('compose');
  const [targetMode, setTargetMode] = useState<TargetMode>('all');
  const [channel, setChannel] = useState<Channel>('inbox');
  const [targetRoles, setTargetRoles] = useState<string[]>([]);
  const [targetTraining, setTargetTraining] = useState<string | null>(null);
  const [targetClass, setTargetClass] = useState<string | null>(null);
  const [targetUsers, setTargetUsers] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<SendResult | null>(null);
  const [expandedBatch, setExpandedBatch] = useState<string | null>(null);

  useEffect(() => {
    loadUsers(); loadTrainings(); loadTrainingRecords(); loadClassrooms(); loadMessages();
  }, []);

  const activeUsers = useMemo(() => users.filter(u => u.active !== false), [users]);

  const userOptions = useMemo(() =>
    activeUsers.map(u => ({
      value: String(u.id),
      label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
    })),
  [activeUsers]);

  const trainingOptions = useMemo(() =>
    trainings.filter(t => t.active !== '0').map(t => ({ value: String(t.id), label: t.name })),
  [trainings]);

  const classOptions = useMemo(() =>
    classrooms
      .filter(c => c.status !== 'cancelled')
      .sort((a, b) => (a.classDate || '').localeCompare(b.classDate || ''))
      .map(c => {
        const dateStr = c.classDate ? dayjs(c.classDate).format('MMM D') : 'TBD';
        const regCount = (() => { try { return JSON.parse(c.registeredUsers || '[]').length; } catch { return 0; } })();
        return { value: String(c.id), label: `${c.title} — ${dateStr} (${regCount} students)` };
      }),
  [classrooms]);

  // Resolve target label for history
  function getTargetLabel(): string {
    switch (targetMode) {
      case 'all': return 'All Users';
      case 'role': return `Roles: ${targetRoles.join(', ')}`;
      case 'training': return trainingOptions.find(t => t.value === targetTraining)?.label || 'Training';
      case 'class': return classOptions.find(c => c.value === targetClass)?.label || 'Class';
      case 'specific': return `${targetUsers.length} selected`;
      default: return '';
    }
  }

  // Resolve recipients
  const recipients = useMemo(() => {
    let targets = activeUsers;
    switch (targetMode) {
      case 'role':
        targets = activeUsers.filter(u => targetRoles.includes(u.role));
        break;
      case 'training': {
        if (!targetTraining) return [];
        const enrolledIds = new Set(trainingRecords.filter(r => String(r.trainingId) === targetTraining).map(r => r.userId));
        targets = activeUsers.filter(u => enrolledIds.has(String(u.id)));
        break;
      }
      case 'class': {
        if (!targetClass) return [];
        const cls = classrooms.find(c => String(c.id) === targetClass);
        if (!cls) return [];
        try {
          const registered: Array<{ userId: string; email: string; userName: string }> = JSON.parse(cls.registeredUsers || '[]');
          const regEmails = new Set(registered.map(r => r.email?.toLowerCase()).filter(Boolean));
          const regIds = new Set(registered.map(r => r.userId));
          targets = activeUsers.filter(u => regEmails.has(u.email.toLowerCase()) || regIds.has(String(u.id)));
          const centralEmails = new Set(targets.map(t => t.email.toLowerCase()));
          for (const r of registered) {
            if (r.email && !centralEmails.has(r.email.toLowerCase())) {
              targets.push({
                id: '0', email: r.email,
                firstName: r.userName?.split(' ')[0] || '', lastName: r.userName?.split(' ').slice(1).join(' ') || '',
                role: 'student', active: true, phoneNumber: '', fullName: '',
              });
            }
          }
        } catch { /* */ }
        break;
      }
      case 'specific':
        targets = activeUsers.filter(u => targetUsers.includes(String(u.id)));
        break;
    }
    return targets.filter(u => u.email?.trim());
  }, [targetMode, targetRoles, targetTraining, targetClass, targetUsers, activeUsers, trainingRecords, classrooms]);

  // ── Send handler ────────────────────────────────────────────
  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      notifications.show({ message: 'Subject and message are required', color: '#C68700' });
      return;
    }
    if (recipients.length === 0) {
      notifications.show({ message: 'No recipients selected', color: '#C68700' });
      return;
    }
    const channelLabel = channel === 'both' ? 'inbox + email' : channel;
    if (!confirm(`Send ${channelLabel} message to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}?`)) return;

    setSending(true);
    setProgress(0);
    setResult(null);

    const batchId = `batch_${Date.now()}`;
    const now = new Date().toISOString();
    const senderName = user?.fullName || user?.email || 'Admin';
    const senderEmail = user?.email || '';
    const targetLabel = getTargetLabel();
    const sendResult: SendResult = { total: recipients.length, sent: 0, failed: 0, errors: [] };

    // Build inbox messages
    if (channel === 'inbox' || channel === 'both') {
      const inboxMsgs: Omit<BBMessage, 'id'>[] = recipients.map(u => {
        const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
        return {
          recipientEmail: u.email,
          recipientName: name || u.email,
          senderName, senderEmail,
          subject,
          body: message.replace(/\{name\}/g, name || 'Student'),
          channel: channel as BBMessage['channel'],
          targetMode, targetLabel,
          readAt: '',
          sentAt: now,
          batchId,
        };
      });
      await sendMessages(inboxMsgs);
      sendResult.sent += inboxMsgs.length;
    }

    // Send emails
    if (channel === 'email' || channel === 'both') {
      const BATCH = 5;
      for (let i = 0; i < recipients.length; i += BATCH) {
        const batch = recipients.slice(i, i + BATCH);
        await Promise.all(batch.map(async (u) => {
          try {
            const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
            const resp = await fetch(`${WORKER_BASE}/api/email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Origin: ORIGIN_HEADER },
              body: JSON.stringify({ to: u.email, subject, body: message.replace(/\{name\}/g, name || 'Student') }),
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            if (channel === 'email') sendResult.sent++;
          } catch (err) {
            sendResult.failed++;
            sendResult.errors.push(`${u.email}: ${err instanceof Error ? err.message : 'Failed'}`);
          }
        }));
        setProgress(Math.min(((i + BATCH) / recipients.length) * 100, 100));
      }

      // Log email-only sends to message history too
      if (channel === 'email') {
        const logMsgs: Omit<BBMessage, 'id'>[] = recipients.map(u => ({
          recipientEmail: u.email,
          recipientName: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
          senderName, senderEmail, subject,
          body: message.replace(/\{name\}/g, `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Student'),
          channel: 'email', targetMode, targetLabel, readAt: '', sentAt: now, batchId,
        }));
        await sendMessages(logMsgs);
      }
    }

    setResult(sendResult);
    setSending(false);
    const total = channel === 'both' ? recipients.length : sendResult.sent;
    notifications.show({
      title: 'Sent',
      message: `${total} message${total > 1 ? 's' : ''} sent via ${channelLabel}.` +
        (sendResult.failed > 0 ? ` ${sendResult.failed} email(s) failed.` : ''),
      color: sendResult.failed > 0 ? '#F86A0B' : 'var(--color-success)',
    });
  };

  // ── Sent history ────────────────────────────────────────────
  const sentBatches = useMemo(() => {
    const batchMap = new Map<string, { batchId: string; subject: string; senderName: string; channel: string; targetLabel: string; sentAt: string; count: number; readCount: number }>();
    for (const m of messages) {
      if (!m.batchId) continue;
      const existing = batchMap.get(m.batchId);
      if (existing) {
        existing.count++;
        if (m.readAt) existing.readCount++;
      } else {
        batchMap.set(m.batchId, {
          batchId: m.batchId, subject: m.subject, senderName: m.senderName,
          channel: m.channel, targetLabel: m.targetLabel, sentAt: m.sentAt,
          count: 1, readCount: m.readAt ? 1 : 0,
        });
      }
    }
    return [...batchMap.values()].sort((a, b) => b.sentAt.localeCompare(a.sentAt));
  }, [messages]);

  if (!canEdit) {
    return <Paper p="xl" ta="center"><Text c="dimmed">You do not have permission to send communications.</Text></Paper>;
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3} c="black">Communications</Title>
          <Text size="sm" c="dimmed">Send messages to students and staff via inbox or email</Text>
        </div>
        <Button variant="light" size="xs" leftSection={<IconRefresh size={14} />}
          onClick={() => { loadUsers(); loadTrainings(); loadTrainingRecords(); loadClassrooms(); loadMessages(); }}>
          Refresh
        </Button>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="compose" leftSection={<IconSend size={14} />}>Compose</Tabs.Tab>
          <Tabs.Tab value="history" leftSection={<IconHistory size={14} />}>
            Sent History ({sentBatches.length})
          </Tabs.Tab>
        </Tabs.List>

        {/* ── COMPOSE TAB ─────────────────────────────────────── */}
        <Tabs.Panel value="compose">
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Stack gap="md">
              {/* Recipients */}
              <Paper p="md" withBorder>
                <Stack gap="sm">
                  <Text fw={600} size="sm">Recipients</Text>
                  <SegmentedControl value={targetMode} onChange={v => setTargetMode(v as TargetMode)}
                    data={[
                      { value: 'all', label: 'All' },
                      { value: 'role', label: 'Role' },
                      { value: 'training', label: 'Training' },
                      { value: 'class', label: 'Class' },
                      { value: 'specific', label: 'Select' },
                    ]} fullWidth size="sm" />

                  {targetMode === 'role' && (
                    <MultiSelect label="Roles" data={[
                      { value: 'student', label: 'Students' }, { value: 'trainer', label: 'Trainers' }, { value: 'admin', label: 'Admins' },
                    ]} value={targetRoles} onChange={setTargetRoles} placeholder="Select roles" size="sm" />
                  )}
                  {targetMode === 'training' && (
                    <Select label="Enrolled in Training" data={trainingOptions} value={targetTraining}
                      onChange={setTargetTraining} placeholder="Select training" size="sm" searchable />
                  )}
                  {targetMode === 'class' && (
                    <Select label="Class" data={classOptions} value={targetClass}
                      onChange={setTargetClass} placeholder="Select a class" size="sm" searchable />
                  )}
                  {targetMode === 'specific' && (
                    <MultiSelect label="Users" data={userOptions} value={targetUsers}
                      onChange={setTargetUsers} placeholder="Select users" size="sm" searchable />
                  )}

                  <Group gap="sm">
                    <Badge variant="light" color={recipients.length > 0 ? 'var(--color-primary-700)' : '#939598'} size="lg">
                      <Group gap={4}><IconUsers size={12} /> {recipients.length} recipient{recipients.length !== 1 ? 's' : ''}</Group>
                    </Badge>
                  </Group>
                </Stack>
              </Paper>

              {/* Channel + Compose */}
              <Paper p="md" withBorder>
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text fw={600} size="sm">Delivery Channel</Text>
                  </Group>
                  <SegmentedControl value={channel} onChange={v => setChannel(v as Channel)}
                    data={[
                      { value: 'inbox', label: 'In-App Inbox' },
                      { value: 'email', label: 'Email' },
                      { value: 'both', label: 'Both' },
                    ]} fullWidth size="sm" />
                  {channel === 'email' && (
                    <Text size="xs" c="#F86A0B">Email delivery requires AWS SES to be configured on the worker.</Text>
                  )}

                  <TextInput label="Subject" placeholder="Training announcement..." value={subject}
                    onChange={e => setSubject(e.target.value)} required />
                  <Textarea label="Message" placeholder="Write your message here. Use {name} for personalization."
                    value={message} onChange={e => setMessage(e.target.value)} minRows={5} autosize required />
                  <Text size="xs" c="dimmed">Tip: Use <code>{'{name}'}</code> to insert the recipient's name.</Text>

                  {sending && <Progress value={progress} size="sm" color="var(--color-primary-700)" animated />}

                  <Group justify="flex-end">
                    <Button leftSection={channel === 'email' ? <IconMail size={16} /> : <IconInbox size={16} />}
                      onClick={handleSend} loading={sending}
                      disabled={recipients.length === 0 || !subject.trim() || !message.trim()}>
                      Send to {recipients.length} via {channel === 'both' ? 'Inbox + Email' : channel === 'inbox' ? 'Inbox' : 'Email'}
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            </Stack>

            {/* Right column */}
            <Stack gap="md">
              <Paper p="md" withBorder>
                <Text fw={600} size="sm" mb="sm">Preview</Text>
                {subject || message ? (
                  <Paper p="md" bg="gray.0" radius="sm">
                    <Group gap="xs" mb="xs">
                      <Badge size="xs" variant="light" color={channel === 'email' ? 'var(--color-primary-700)' : channel === 'both' ? 'var(--data-viz-650)' : '#03AC9E'}>
                        {channel === 'both' ? 'Inbox + Email' : channel}
                      </Badge>
                    </Group>
                    <Text fw={600} mb="xs">{subject || '(No subject)'}</Text>
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{message.replace(/\{name\}/g, 'Jane Smith') || '(No message)'}</Text>
                  </Paper>
                ) : (
                  <Text size="sm" c="dimmed" ta="center" py="lg">Compose a message to see a preview.</Text>
                )}
              </Paper>

              {result && (
                <Paper p="md" withBorder>
                  <Text fw={600} size="sm" mb="sm">Send Results</Text>
                  <SimpleGrid cols={{ base: 1, sm: 3 }}>
                    <Paper p="sm" ta="center" style={{ borderTop: '3px solid #939598' }}>
                      <Text size="xs" c="dimmed">Total</Text><Text fw={700} size="lg">{result.total}</Text>
                    </Paper>
                    <Paper p="sm" ta="center" style={{ borderTop: '3px solid var(--color-success)' }}>
                      <Text size="xs" c="dimmed">Sent</Text><Text fw={700} size="lg" c="var(--color-success)">{result.sent}</Text>
                    </Paper>
                    <Paper p="sm" ta="center" style={{ borderTop: '3px solid var(--color-error)' }}>
                      <Text size="xs" c="dimmed">Failed</Text><Text fw={700} size="lg" c="var(--color-error)">{result.failed}</Text>
                    </Paper>
                  </SimpleGrid>
                  {result.errors.length > 0 && (
                    <Paper p="sm" mt="sm" bg="red.0" radius="sm">
                      <Text size="xs" fw={600} c="var(--color-error)" mb={4}>Errors:</Text>
                      {result.errors.map((e, i) => <Text key={i} size="xs" c="red.7">{e}</Text>)}
                    </Paper>
                  )}
                </Paper>
              )}

              <Paper p="md" withBorder>
                <Text fw={600} size="sm" mb="sm">Quick Templates</Text>
                <Stack gap="xs">
                  {[
                    { label: 'Training Reminder', subj: 'Training Reminder', body: 'Hi {name},\n\nThis is a reminder that you have upcoming training requirements. Please log in to Blackboard to review your assigned trainings and ensure you complete them before the deadline.\n\nThank you.' },
                    { label: 'New Class Available', subj: 'New Training Class Available', body: 'Hi {name},\n\nA new training class has been added to the schedule. Please visit the Classrooms page in Blackboard to view details and register.\n\nSpaces are limited — register early to secure your spot.' },
                    { label: 'Certificate Expiring', subj: 'Training Certificate Expiring Soon', body: 'Hi {name},\n\nOne or more of your training certificates is expiring soon. Please visit Blackboard to review your certifications and schedule any required recertification training.\n\nMaintaining current certifications is required for your position.' },
                    { label: 'General Announcement', subj: 'Blackboard Announcement', body: 'Hi {name},\n\n' },
                  ].map(t => (
                    <Button key={t.label} variant="light" size="xs" fullWidth justify="flex-start"
                      leftSection={<IconMail size={12} />}
                      onClick={() => { setSubject(t.subj); setMessage(t.body); }}>
                      {t.label}
                    </Button>
                  ))}
                </Stack>
              </Paper>
            </Stack>
          </SimpleGrid>
        </Tabs.Panel>

        {/* ── SENT HISTORY TAB ────────────────────────────────── */}
        <Tabs.Panel value="history">
          <Stack gap="sm">
            {sentBatches.length === 0 && (
              <Paper p="xl" withBorder ta="center">
                <Text size="sm" c="dimmed">No messages sent yet.</Text>
              </Paper>
            )}
            {sentBatches.map(b => {
              const isExpanded = expandedBatch === b.batchId;
              const batchMessages = messages.filter(m => m.batchId === b.batchId);
              // Get the body from the first message
              const bodyPreview = batchMessages[0]?.body || '';
              return (
                <Paper key={b.batchId} withBorder radius="sm" style={{ overflow: 'hidden' }}>
                  {/* Header row */}
                  <Group p="sm" justify="space-between" style={{ cursor: 'pointer', background: isExpanded ? '#F8F8F8' : undefined }}
                    onClick={() => setExpandedBatch(isExpanded ? null : b.batchId)}>
                    <Group gap="sm" style={{ flex: 1 }}>
                      {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                      <Text size="sm" fw={600}>{b.subject}</Text>
                      <Badge size="xs" variant="light"
                        color={b.channel === 'email' ? 'var(--color-primary-700)' : b.channel === 'both' ? 'var(--data-viz-650)' : '#03AC9E'}>
                        {b.channel === 'both' ? 'Inbox + Email' : b.channel}
                      </Badge>
                      <Badge size="xs" variant="light" color="#939598">{b.count} recipients</Badge>
                      <Text size="xs" c={b.readCount === b.count ? 'var(--color-success)' : b.readCount > 0 ? '#F86A0B' : 'dimmed'}>
                        {b.readCount}/{b.count} read
                      </Text>
                    </Group>
                    <Group gap="sm">
                      <Text size="xs" c="dimmed">{b.senderName}</Text>
                      <Text size="xs" c="dimmed">{b.sentAt ? dayjs(b.sentAt).format('MMM D, h:mm A') : '-'}</Text>
                    </Group>
                  </Group>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid #e2e8f0' }}>
                      {/* Message body */}
                      <Paper p="md" m="sm" bg="gray.0" radius="sm">
                        <Text size="xs" fw={600} c="dimmed" mb={4}>Message</Text>
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{bodyPreview}</Text>
                      </Paper>

                      {/* Target info */}
                      <Group px="sm" pb="xs" gap="xs">
                        <Text size="xs" c="dimmed">Target:</Text>
                        <Badge size="xs" variant="outline">{b.targetLabel}</Badge>
                      </Group>

                      {/* Recipients table */}
                      <Table striped fz="xs" style={{ borderTop: '1px solid #e2e8f0' }}>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Recipient</Table.Th>
                            <Table.Th>Email</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Read At</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {batchMessages.map(m => (
                            <Table.Tr key={m.id}>
                              <Table.Td>{m.recipientName || '-'}</Table.Td>
                              <Table.Td><Text size="xs" c="dimmed">{m.recipientEmail}</Text></Table.Td>
                              <Table.Td>
                                {m.readAt ? (
                                  <Badge size="xs" color="var(--color-success)" variant="light" leftSection={<IconCheck size={8} />}>Read</Badge>
                                ) : (
                                  <Badge size="xs" color="#939598" variant="light" leftSection={<IconClock size={8} />}>Unread</Badge>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Text size="xs" c="dimmed">{m.readAt ? dayjs(m.readAt).format('MMM D, h:mm A') : '-'}</Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </div>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
